import { useState, useEffect } from 'react';

export type Role = 'advertiser' | 'publisher';

interface RoleSelectionHook {
  role: Role | null;
  setRole: (role: Role) => void;
  resetPlayground: () => void;
}

/**
 * Hook for managing role selection in the playground
 * Persists selected role and associated data in localStorage
 */
const useRoleSelection = (): RoleSelectionHook => {
  const [role, setRoleState] = useState<Role | null>(() => {
    try {
      const savedRole = localStorage.getItem('playground_role');
      return savedRole ? JSON.parse(savedRole) : null;
    } catch (error) {
      console.error('Error loading role from localStorage:', error);
      return null;
    }
  });

  // Update localStorage when role changes
  useEffect(() => {
    if (role) {
      localStorage.setItem('playground_role', JSON.stringify(role));
    } else {
      localStorage.removeItem('playground_role');
    }
  }, [role]);

  /**
   * Set the current role and initialize role-specific state if needed
   * @param newRole - The role to set ('advertiser' or 'publisher')
   */
  const setRole = (newRole: Role) => {
    setRoleState(newRole);

    // Initialize role-specific data in localStorage if it doesn't exist
    const configKey = `playground_${newRole}_config`;
    if (!localStorage.getItem(configKey)) {
      if (newRole === 'advertiser') {
        localStorage.setItem(configKey, JSON.stringify({
          input: '',
          persona: 'professional',
          tone: 'neutral',
          variants: []
        }));
      } else {
        localStorage.setItem(configKey, JSON.stringify({
          framework: 'fastapi',
          placement: 'after',
          advanced: false
        }));
      }
    }
  };

  /**
   * Reset all playground data in localStorage
   */
  const resetPlayground = () => {
    // Clear role selection
    localStorage.removeItem('playground_role');
    
    // Clear role-specific configurations
    localStorage.removeItem('playground_advertiser_config');
    localStorage.removeItem('playground_publisher_config');
    
    // Clear analytics data
    localStorage.removeItem('playground_analytics');
    
    // Reset state
    setRoleState(null);
  };

  return { role, setRole, resetPlayground };
};

export default useRoleSelection; 